'use client';

import React, { useMemo, useState } from 'react';
import { faStethoscope } from '@fortawesome/pro-solid-svg-icons';
import useFilterSets from 'src/hooks/filters/useFilterSets';
import { useGetGPOAffiliationsSuggestionsInfiniteQuery } from 'src/api/redux';
import {
  FilterIcon,
  IncludeExcludeSelect,
  ConnectorSelect,
  FilterAutocomplete,
  Divider,
  FilterLabel,
  ClearFilterButton,
} from 'src/components/FilterSets/components';
import { SelectedFilters } from 'src/components/FilterSets/SelectedFilters';
import type { FilterValue } from 'src/context/reducers/filterSetReducer';

const GPOAffiliations = ({ location }: { location: string }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const { filterSet, updateFilter, removeFilterValue } =
    useFilterSets(location);
  const filterValue = filterSet.gpoAffiliations;

  const { data, isLoading, isFetching, hasNextPage, fetchNextPage } =
    useGetGPOAffiliationsSuggestionsInfiniteQuery({
      q: searchTerm,
    });

  const options = useMemo(() => {
    const allResults =
      data?.pages.flatMap((page) => page.results) ?? [];
    return allResults
      .slice()
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((result) => ({
        value: result.id,
        label: result.name,
      }));
  }, [data]);

  const handleRemoveFilter = (filterLabel: string) => {
    removeFilterValue('gpoAffiliations', filterLabel);
  };

  const handleFilterChange = (value: FilterValue) => {
    updateFilter('gpoAffiliations', value);
  };

  const handleSearch = (term: string) => {
    setSearchTerm(term);
  };

  const hasValues = filterValue?.values?.length;

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center justify-between">
        <FilterLabel>GPO Affiliations</FilterLabel>
        <ClearFilterButton
          type="gpoAffiliations"
          isDisabled={!hasValues}
          location={location}
        />
      </div>
      <div className="self-stretch px-4 py-2 bg-white rounded-full outline outline-1 -outline-offset-1 outline-neutral-300 inline-flex justify-between items-center">
        <div className="flex-1 flex justify-start items-center gap-2">
          <FilterIcon icon={faStethoscope} />
          <IncludeExcludeSelect
            title="GPO Affiliations"
            value={filterValue}
            onChange={handleFilterChange}
          />
          <Divider />
          <ConnectorSelect
            title="GPO Affiliations"
            value={filterValue}
            onChange={handleFilterChange}
            isAll={filterValue?.is_all}
          />
          <Divider />
          <FilterAutocomplete
            options={options}
            value={filterValue}
            onChange={handleFilterChange}
            isLoading={isLoading}
            placeholder="Search GPO affiliations..."
            onSearch={handleSearch}
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetching}
            fetchNextPage={fetchNextPage}
          />
        </div>
      </div>
      <SelectedFilters
        filters={filterValue?.values.map((v) => v.label)}
        onRemove={handleRemoveFilter}
        isAll={filterValue?.is_all}
      />
    </div>
  );
};

export default GPOAffiliations;
